import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { checkRateLimit, updateRateLimit, RATE_LIMIT_KEYS } from '../utils/rateLimiter';
import './FeatureRequest.css';

function FeatureRequest() {
  const [submitted, setSubmitted] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [formError, setFormError] = useState('');
  const [requestType, setRequestType] = useState('feature');
  const [prefillTitle, setPrefillTitle] = useState('');
  const [prefillEmail, setPrefillEmail] = useState('');
  
  // Get location to access navigation state
  const location = useLocation();

  // Set prefill values from navigation state if available
  useEffect(() => {
    if (location.state) {
      if (location.state.subject) {
        setPrefillTitle(location.state.subject);
      }
      if (location.state.email) {
        setPrefillEmail(location.state.email);
      }
    }
  }, [location]);

  // Check and update rate limit timer
  useEffect(() => {
    const timer = setInterval(() => {
      const { remainingTime } = checkRateLimit(RATE_LIMIT_KEYS.FEATURE_REQUEST);
      setRemainingTime(remainingTime);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = e => {
    e.preventDefault();
    setFormError('');

    // Check rate limit
    const { allowed, remainingTime } = checkRateLimit(RATE_LIMIT_KEYS.FEATURE_REQUEST);
    if (!allowed) {
      setFormError(`Please wait ${remainingTime} seconds before submitting again.`);
      return;
    }

    // Get form data
    const formData = new FormData(e.target);

    // Basic form validation
    const email = formData.get('email');
    const description = formData.get('description');
    const title = formData.get('title');

    if (title.length < 3) {
      setFormError('Title must be at least 3 characters long.');
      return;
    }

    if (description.length < 10) {
      setFormError('Description must be at least 10 characters long.');
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      setFormError('Please enter a valid email address.');
      return;
    }

    // Update rate limit timestamp
    updateRateLimit(RATE_LIMIT_KEYS.FEATURE_REQUEST);

    // Submit the form
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData).toString(),
    })
      .then(() => setSubmitted(true))
      .catch(() => setFormError('Failed to send request. Please try again later.'));
  };

  if (submitted) {
    return (
      <div className="full-page-wrapper" style={{
        background: 'linear-gradient(to left bottom, #777777, #1a1a1a)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}>
        <div className="success-message">
          <h3>Thank you for your {requestType === 'feature' ? 'feature request' : 'bug report'}!</h3>
          <p>We will review your submission and take appropriate action.</p>
          <button onClick={() => setSubmitted(false)} disabled={remainingTime > 0}>
            {remainingTime > 0
              ? `Wait ${remainingTime}s to submit another request`
              : 'Submit another request'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="full-page-wrapper"
      style={{
        background: 'linear-gradient(to left bottom, #777777, #1a1a1a)',
        width: '100%',
      }}
    >
      <div className="feature-request-form-container">
        <form
          name="feature-request"
          method="POST"
          netlify="true"
          netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          className="feature-request-form"
        >
          <input type="hidden" name="form-name" value="feature-request" />
          <p hidden>
            <label>
              Do not fill this out if you are human: <input name="bot-field" />
            </label>
          </p>

          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label className="radio-group">Request Type:</label>
            <div className="radio-options">
              <label>
                <input
                  type="radio"
                  name="type"
                  value="feature"
                  checked={requestType === 'feature'}
                  onChange={e => setRequestType(e.target.value)}
                />
                Feature Request
              </label>
              <label>
                <input
                  type="radio"
                  name="type"
                  value="bug"
                  checked={requestType === 'bug'}
                  onChange={e => setRequestType(e.target.value)}
                />
                Bug Report
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="title">Title:</label>
            <input
              type="text"
              id="title"
              name="title"
              required
              defaultValue={prefillTitle}
              placeholder={
                requestType === 'feature'
                  ? 'Brief description of the feature'
                  : 'Brief description of the bug'
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input 
              type="email" 
              id="email" 
              name="email" 
              required 
              defaultValue={prefillEmail}
              placeholder="Your email address" 
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description:</label>
            <textarea
              id="description"
              name="description"
              required
              placeholder={
                requestType === 'feature'
                  ? 'Describe the feature you would like to see added...'
                  : 'Describe the bug, including steps to reproduce it...'
              }
            ></textarea>
          </div>

          {requestType === 'bug' && (
            <div className="form-group">
              <label htmlFor="browser">Browser & Version:</label>
              <input
                type="text"
                id="browser"
                name="browser"
                placeholder="e.g., Chrome 91, Firefox 89"
              />
            </div>
          )}

          <div data-netlify-recaptcha="true"></div>

          <button type="submit" disabled={remainingTime > 0}>
            {remainingTime > 0
              ? `Wait ${remainingTime}s to submit again`
              : `Submit ${requestType === 'feature' ? 'Feature Request' : 'Bug Report'}`}
          </button>
        </form>
      </div>
    </div>
  );
}

export default FeatureRequest;
